import type { ModuleSnapshot } from '@ctcells/contract'
import { enumLabel, intlLocale, t } from '@ctcells/i18n'

import { METRIC_SPECS, type MetricSpec } from './metricSpecs.ts'
import { reagentStorageReading, telemetryString } from './monitoringModel.ts'
import { poseValueOf } from './robotPoseModel.ts'

/**
 * 영상 «아래»에 메트릭을 세우는 블록 — 장비별 보기에서 이 블록의 메트릭은 상세 카드 메트릭 구역이 아니라 비전 칸 바로 밑에
 * «언제나» 선다(eric 2026-10-06 「원심분리기 하단에.. 메트릭값 보이게 할 수 잇어?」 → 「현재 메트릭을... 원심분리기.. 영상 아래에다가」).
 * 원심분리기(`cent`) · 어셈블러 본체(`mod` — eric 2026-10-08 「어셈블러 메트릭 영역에서 왜 메트릭 데이터가 아무 것도 안 보여?」 · dev2 `telemetry` 가
 * `{grip_drop_policy …}` 뿐이라 상세 카드 구역이 통째로 사라졌다 · BR-120). 염색기는 종전대로 상세 카드다(시약 보관 온도가 실제로 온다). 늘릴 때 여기 한 줄을 더한다.
 */
export const FIGURE_METRIC_BLOCKS: ReadonlySet<string> = new Set(['cent', 'mod'])

/**
 * 영상 아래 메트릭에 **장비마다 덧붙이는 칸** — 모듈 안 3축 로봇의 X · Y · Z 와 끝장치 상태(eric 2026-10-08 「원심분리기와 어셈블러 염색기..
 * 3축 로봇의 x, y, z 와 기타의 값을 메트릭 값에 넣어줘」).
 * 🔴 X · Y · Z 는 **위치 봉투**(`GET /modules` 목록의 `pose_telemetry` · v0.9.4)에서 읽는다 — `<robot_id>.x_mm` … 의 이름(`METRIC_SPECS.gantry`).
 *    받은 봉투가 없으면(`null` · mock 모듈) 종전대로 「미수신」(지어내지 않는다).
 *    모의 값(`pose_simulated: 1`)이어도 **값 그대로** 보인다 — 「위치는 모사값」 표지는 eric 이 걷었다(2026-10-12 「위치는 모사값 빼고」).
 *    🔴 모사 값을 「—」(미수신)로 접지 않는다 — 콘솔 · 모듈 앱이 같은 값을 보인다(2026-10-26 · 모듈 앱의 `hideSimulated` 접기를 걷었다).
 *    부품 미러의 `devices[].simulated` 도 같다 — 값을 비우지 않는다. 칸마다 배지로 되살리지도 마라(같은 표지를 다른 자리에 다는 셈).
 *    ⚠ 2026-10-08 ~ 10-11 에는 서버가 낸 적 없는 `telemetry.gantry_*_mm` 를 찾아 늘 「미수신」이었다(BR-122).
 * 끝장치 — 원심 · 어셈블러는 그리퍼 파지(`grips[]` · RGD-35-30), 염색기는 피펫 팁 장착(`tip.mounted` · ZEUS LT). 둘 다 서버가 실제로 준다.
 */
export const FIGURE_METRIC_EXTRA: Readonly<Record<string, readonly MetricSpec[]>> = {
  cent: [
    ...METRIC_SPECS_GANTRY(),
    { key: 'grip', labelKey: 'metric.gripper', field: 'state', source: 'grips' },
  ],
  asm: [
    ...METRIC_SPECS_GANTRY(),
    { key: 'grip', labelKey: 'metric.gripper', field: 'state', source: 'grips' },
  ],
  stain: [
    ...METRIC_SPECS_GANTRY(),
    { key: 'tipMounted', labelKey: 'metric.tipMounted', field: 'mounted', source: 'tip' },
  ],
}

/** 「3축 로봇」 묶음에 드는 칸 — 3축 좌표 · 끝장치 상태(`FIGURE_METRIC_EXTRA` 의 출처) */
const GANTRY_SOURCES: ReadonlySet<string> = new Set(['pose', 'grips', 'tip'])

function METRIC_SPECS_GANTRY(): readonly MetricSpec[] {
  return METRIC_SPECS['gantry'] ?? []
}

/** 영상 아래 타일 한 칸. 값과 단위를 따로 지닌다 — 숫자는 핵심 수치 단, 단위는 보조 글자로 선다 */
export interface FigureMetricTile {
  key: string
  label: string
  /** 숫자(서버 값 그대로 · 천 단위 구분). `null` 이면 「미수신」 */
  value: string | null
  unit?: string | undefined
  /** 위치 봉투의 모의 값(`pose_simulated: 1`) — 모델의 사실로만 지닌다. 화면에 표지를 그리지 않는다(eric 2026-10-12 걷음) */
  simulated?: boolean | undefined
  /**
   * 🔴 응답에 «칸이 없다»(키 자체가 없음). `null` 값과 가른다 — 칸이 있는데 `null` 이면 모듈이 «아직 못 쟀다»이고,
   *    칸이 없으면 서버가 그 값을 «보내지 않는다». 둘 다 「미수신」이지만 사유 줄은 칸이 없는 것만 적는다.
   */
  absent: boolean
  /** 「3축 로봇」 묶음 칸인가(3축 좌표 · 그리퍼 · 피펫 팁) */
  gantry?: boolean | undefined
  /** 시약 보관 온도 칸인가(염색기 cold rack — 상자에서 「cold rack」 묶음으로 선다) */
  storage?: boolean | undefined
}

/**
 * 영상 아래 메트릭 — 🔴 **상세 카드 메트릭 구역(`metricTiles`)과 다르게 칸이 없어도 타일을 세운다.**
 * eric 이 «그 자리에 메트릭을 보이라»고 콕 집었으므로 자리는 서고, 값이 없으면 「미수신」 + 사유 줄이다
 * (2026-10-06 실측 — dev2 원심분리기 `telemetry` 는 IDLE · 회전 중 모두 `{grip_drop_policy}` 하나 · BR-115).
 * 🔴 **값을 지어내지 않는다** — 레시피 설정값(`speed_rpm` · `duration_sec`)은 «측정»이 아니라 여기 싣지 않는다.
 * ⚠ `telemetry` 주머니만 본다 — 원심분리기 표(`METRIC_SPECS.cent`)는 소모품 · 시약 보관 칸이 없다.
 */
export function figureMetricTiles(
  snapshot: ModuleSnapshot | undefined,
  spec: readonly MetricSpec[],
): FigureMetricTile[] {
  const bag = snapshot?.telemetry
  return spec
    .filter(
      (item) =>
        ['telemetry', 'grips', 'tip', 'pose'].includes(item.source ?? 'telemetry') ||
        /* 시약 보관 온도 — 능력이 없는 모듈(`reagent_storage` 칸 부재)이면 줄째 없다(상세 카드 구역과 같은 규칙 · 2026-10-12 상자로 옮김) */
        (item.source === 'reagent_storage' &&
          reagentStorageReading(snapshot, item.field).kind !== 'absent'),
    )
    .map((item) => {
      if (item.source === 'reagent_storage') {
        const reading = reagentStorageReading(snapshot, item.field)
        return {
          key: item.key,
          label: t(item.labelKey),
          value: reading.kind === 'value' ? reading.text : null,
          unit: item.unit,
          absent: reading.kind !== 'value',
        }
      }
      if (item.source === 'pose') {
        /* 위치 봉투 — 🔴 못 받았으면 칸 부재(「0」으로 접지 않는다) · 값은 서버 숫자 그대로(천 단위 구분) */
        const reading = poseValueOf(snapshot, item.field)
        return reading.kind === 'absent'
          ? { key: item.key, label: t(item.labelKey), value: null, unit: item.unit, absent: true }
          : {
              key: item.key,
              label: t(item.labelKey),
              value: reading.value.toLocaleString(intlLocale()),
              unit: item.unit,
              absent: false,
              simulated: reading.simulated,
            }
      }
      if (item.source === 'grips') {
        /* 그리퍼 파지 — 첫 그리퍼의 상태 이름(쥐고 있음 · 빈손 …). 목록이 없으면 칸이 없다(미수신) */
        const state = snapshot?.grips?.[0]?.state
        return {
          key: item.key,
          label: t(item.labelKey),
          value: state === undefined ? null : enumLabel('enum.gripState', state),
          /* 빈 목록도 «칸 없음» — 그리퍼 판독을 안 냈다 */
          absent: (snapshot?.grips?.length ?? 0) === 0,
        }
      }
      if (item.source === 'tip') {
        /* 피펫 팁 장착 — 🔴 모르면 칸 부재(「모름」을 「없음」으로 접지 않는다 · 계약 문면) */
        const tip = snapshot?.tip
        return {
          key: item.key,
          label: t(item.labelKey),
          value:
            tip == null ? null : t(tip.mounted ? 'metric.tipMounted.on' : 'metric.tipMounted.off'),
          absent: tip == null,
        }
      }
      return {
        key: item.key,
        label: t(item.labelKey),
        value: telemetryString(snapshot, item.field) ?? null,
        unit: item.unit,
        absent: bag == null || !(item.field in bag),
      }
    })
    .map((tile) => {
      const source = spec.find((item) => item.key === tile.key)?.source
      if (source === 'reagent_storage') return { ...tile, storage: true }
      return source !== undefined && GANTRY_SOURCES.has(source) ? { ...tile, gantry: true } : tile
    })
}
